import React from 'react';
import Moment from 'react-moment';
import Autolinker from 'autolinker';
import { Emoji } from 'emoji-mart';

import { updateMessage, removeMessage, setMessageReaction } from '../actions/messages';
import { updateDoc } from '../actions/docs';

import { trackEvent } from '../services/events-tracker-service';
import { userRoles } from '../constants';
import { getCurrentUser } from '../services/store-service';

import UrlPreview from './url-preview';
import Message from './message';
import Tooltip from './tooltip';
import DocContainer from '../containers/doc-container';
import DocCard from './doc-card';
import DropdownBtn from './dropdown-btn';
import InputTextEditor from './input-text-editor';
import TourStarter from './tour-starter';
import TaskCreateCTA from './task-create-cta';
import MessageThreadCTA from './message-thread-cta';
import MessageAttachment from './message-attachment';
import MessageReactions from './message-reactions';
import CanUser from './can-user';
import MessageInvitePeopleCta from './message-invite-people-cta';
import EmojiPicker from './emoji-picker';

import IconChatBubbleAdd from '../icons/icon-chat-bubble-add';
import IconEmoji from '../icons/icon-emoji';
import IconPin from '../icons/icon-pin';

const CONTROL_ACTION_DELETE = {
    text: 'Delete for everyone',
    style: ['danger'],
    payload: {
        action: 'delete'
    },
    prompt: {
        title: 'DELETE THIS MESSAGE',
        text: 'If you delete this message no one else will ever see it again.',
        confirmText: 'Delete',
        confirmClassName: 'btn-caution'
    }
};

const CONTROL_ACTION_EDIT = {
    text: 'Edit',
    payload: {
        action: 'edit'
    }
};

export default class MessageRich extends Message {
    static defaultProps = {
        dateFormat: 'h:mma',
        urlPreviewEnabled: false,
        clicksEnabled: true
    }

    state = {
        editing: false,
        controlActionDropdownVisible: false
    };

    loadedAttachments = 0;

    handleSubmit = text => {
        if (text === '') {
            this.delete();
            return;
        }

        if (text === this.props.text) {
            this.abortEdit();
            return;
        }

        this.saveEdit(text);
    }

    handleEsc = () => {
        this.abortEdit();
    }

    handleControlActionClick = ({ payload }) => {
        switch (payload.action) {
            case 'edit':
                this.startEdit();
                break;

            case 'delete':
                this.delete();
                break;

            default:
                return null;
        }
    }

    handleBlur = text => {
        this.saveEdit(text);
    }

    handleControlActionDropdownShow = () => {
        this.setState({
            controlActionDropdownVisible: true
        });
    }

    handleControlActionDropdownHide = () => {
        this.setState({
            controlActionDropdownVisible: false
        });
    }

    handleCreateThreadClick = () => {
        this.props.onThreadActivate(this.props.id);

        trackEvent('DISCUSSION.THREAD_START');
    }

    handleActivateThreadClick = () => {
        this.props.onThreadActivate(this.props.id);
    }

    handleReactionSelect = ({ colons }) => {
        setMessageReaction(this.props.id, colons);

        trackEvent('CHAT.REACTION');
    }

    handleReactionRemove = () => {
        setMessageReaction(this.props.id, null);
    }

    handleDeleteClick = () => {
        this.delete();

        this.setState({
            editing: false
        });
    }

    handlePinMessage = () => {
        if (this.props.pinnedMessage === this.props.id) {
            this.props.onPinMessage(null);
        } else {
            this.props.onPinMessage(this.props.id);
        }
    }

    getUserRoleById(roleId) {
        return userRoles().filter(role => role.id === roleId)[0] || { title: 'User Role' };
    }

    startEdit() {
        this.setState({
            editing: true
        }, () => {
            this.props.onEditStart && this.props.onEditStart(this.props.id);
        });

    }

    abortEdit() {
        if (!this.state.editing) {
            return;
        }

        this.setState({
            editing: false
        }, () => {
            this.props.onEditAbort && this.props.onEditAbort();
        });

    }

    saveEdit(text) {
        if (!this.state.editing) {
            return;
        }

        this.setState({
            editing: false
        });

        if (text === this.props.text) {
            return;
        }

        trackEvent('MESSAGE.EDIT');

        updateMessage(this.props.id, { text });
    }

    delete() {
        if (this.props.docAttachment) {
            updateDoc(this.props.docAttachment, {
                archived: true
            });
        }

        removeMessage(this.props.id);

        trackEvent('MESSAGE.REMOVE');
    }

    getTaskName = () => this.contentPane.textContent;

    get controlActions() {
        let actionDelete = CONTROL_ACTION_DELETE;

        if (this.props.docAttachment) {
            actionDelete = {...actionDelete};
            actionDelete.prompt.title = 'DELETE THIS DOC';
            actionDelete.prompt.text = 'Deleting this document here will archive it from your Docs list';
        }

        if (this.props.controlDeleteText) {
            actionDelete = {...actionDelete};
            actionDelete.text = this.props.controlDeleteText;
        }

        return [
            CONTROL_ACTION_EDIT,
            actionDelete
        ];
    }

    setEditorRef = ctrl => this.editorCtrl = ctrl;
    setContentPane = ref => this.contentPane = ref;

    get controlsElement() {
        if (this.props.removed) {
            return null;
        }

        return (
            <div className="message-controls" data-serialize-ignore="true">
                <EmojiPicker onSelect={this.handleReactionSelect}>
                    <div>
                        <Tooltip tooltip="Add reaction">
                            <button className="btn btn-square btn-xxsmall btn-simple btn-icon">
                                <IconEmoji size="14px" />
                            </button>
                        </Tooltip>
                    </div>
                </EmojiPicker>

                {
                    this.isSelf && !this.props.botName && !this.props.system ? (
                        <div className="message-control-item">
                            <DropdownBtn
                                btnClassName="btn btn-square btn-xxsmall btn-simple btn-icon"
                                items={this.controlActions}
                                onDropdownShow={this.handleControlActionDropdownShow}
                                onDropdownHide={this.handleControlActionDropdownHide}
                                onItemClick={this.handleControlActionClick}
                            >
                                <i className="material-icons">edit</i>
                            </DropdownBtn>
                        </div>
                    ) : null
                }

                <CanUser type="createTask">
                    {
                        this.props.canCreateTask && this.hasText ? <TaskCreateCTA getName={this.getTaskName} projectId={this.props.projectId} /> : null
                    }
                </CanUser>

                {
                    this.props.onThreadActivate ? (
                        <Tooltip tooltip="Start a Thread">
                            <button className="btn btn-square btn-xxsmall btn-simple btn-icon" onClick={this.handleCreateThreadClick}>
                                <IconChatBubbleAdd size="14px" />
                            </button>
                        </Tooltip>
                    ) : null
                }

                {
                    this.props.onPinMessage ? (
                        <Tooltip tooltip={this.props.pinnedMessage === this.props.id ? 'Unpin Message' : 'Pin Message'}>
                            <button className="btn btn-square btn-xxsmall btn-simple btn-icon" onClick={this.handlePinMessage}>
                                <IconPin size="14px" />
                            </button>
                        </Tooltip>
                    ) : null
                }
           </div>
        );
    }

    get firstUrl() {
        if (!this.props.text) {
            return null;
        }

        const matches = Autolinker.parse(this.props.text.replace(/(`+(?=[^]+`)[^]+?`+)/g, '%NIFTY_ESC_CODE_REPLACER%'));
        const url = matches && matches[0] && matches[0].url;

        if (url && url.indexOf('http') > 1) {
            return null;
        }

        return url;
    }

    get systemMessageCta() {
        switch (this.props.systemMessageType) {
            case 'tour':
                return <TourStarter />;

            default:
                return null;
        }
    }

    get systemMessageBody() {
        switch (this.props.systemMessageType) {
            case 'hello':
                return (
                    <p>
                        Welcome to Nifty, {getCurrentUser().name}!
                        <br />You're now in the project discussions screen — a hub for all project members to share ideas, discuss progress, and find it all through our powerful search.&nbsp;

                        <CanUser type="inviteToTeam">
                            Discussions aren't too useful without more of your team members, so go ahead and <MessageInvitePeopleCta projectId={this.props.projectId} />.
                        </CanUser>
                    </p>
                );

            case 'due_date':
                return <p>Set this task to be due on <Moment format="MMMM Do, h:mm A">{this.props.systemMessageData.date.iso || this.props.systemMessageData.date}</Moment></p>

            default:
                return this.htmlContent;
        }
    }

    get itemBody() {
        return (
            <div className="message-body">
                <div className="message-body-inner">
                    <div className="message-body-main">
                        {
                            this.props.systemMessageType ? (
                                <div className="pane">
                                    { this.systemMessageBody }
                                </div>
                            ) : (
                                this.state.editing ? (
                                    <InputTextEditor
                                        value={this.props.text}
                                        provideController={this.setEditorRef}
                                        users={this.props.users}
                                        initialState={this.editorState}
                                        autofocus={true}
                                        allowEmptySubmit={true}
                                        onEsc={this.handleEsc}
                                        onBlur={this.handleBlur}
                                        onSubmit={this.handleSubmit}
                                        onUserInvite={this.props.onUserInvite}
                                    />
                                ) : (
                                    <div className="pane" ref={this.setContentPane} onClick={this.handlePaneClick}>
                                        { this.htmlContent }
                                    </div>
                                )
                            )
                        }

                        { this.attachmentsElement }

                        {
                            !this.props.removed && this.props.urlPreviewEnabled && this.firstUrl ? <UrlPreview url={this.firstUrl} /> : null
                        }

                        { this.systemMessageCta }

                        { this.reactionsElement }

                        {
                            !this.props.removed && this.props.replies && this.props.onThreadActivate ? (
                                <MessageThreadCTA id={this.props.id} replies={this.props.replies} repliers={this.props.repliers} onClick={this.handleActivateThreadClick} />
                            ) : null
                        }
                    </div>
                </div>

                <div className="message-controls-helper" data-serialize-ignore="true">
                    Enter to <strong>submit</strong> • Esc to <strong>cancel</strong> • <strong className="clickable" onClick={this.handleDeleteClick}>Delete for everyone</strong>
                </div>
            </div>
        );
    }

    get attachments() {
        return (this.props.attachments || []).filter(attachment => !!attachment);
    }

    get attachmentsElement() {
        if (this.props.removed) {
            return null;
        }

        if (this.props.type === 'document') {            
            return (
                <DocContainer id={this.props.docAttachment}>
                    <DocCard onClick={this.props.onDocClick} />
                </DocContainer>
            )
        }

        if (this.props.attachments && this.props.attachments.length) {
            return  (
                <div className="message-attachments">
                {
                    this.props.attachments
                        .map(({ id }, index) => <MessageAttachment
                            key={id}
                            id={id}
                            filePreviewPath={this.props.filePreviewPath}
                            siblings={[...this.props.attachments.map(attachment => attachment.id)].reverse()}
                        />)
                }
                </div>
            );
        }

        return null;
    }

    get reactionsElement() {
        if (this.props.removed || !this.props.reactions || !this.props.reactions.length) {
            return null;
        }

        return <MessageReactions reactions={this.props.reactions} onReactionRemove={this.handleReactionRemove} onReactionSelect={this.handleReactionSelect} />;
    }

    get hasText() {
        return this.props.text && this.props.text !== '';
    }

    get isSelf() {
        return this.props.user === getCurrentUser().id;
    }

    get className() {
        const className = ['message'];

        this.isSelf && className.push('self');
        this.props.pending && className.push('pending');
        this.props.noMeta && className.push('no-meta');
        this.props.primary && className.push('primary');
        this.props.system && className.push('message-type-system');
        this.props.firstForDay && className.push('first-for-day');
        this.props.pinnedMessage === this.props.id && className.push('pinned');

        (this.state.controlActionDropdownVisible) && className.push('control-action-dropdown-visible');
        this.state.editing && className.push('editing');

        this.messageIsEmoji && className.push('message-emoji-only');
        !this.hasText && className.push('empty-text');

        this.props.error && className.push('error');
        !this.props.removed && this.props.edited && className.push('edited');
        this.props.removed && className.push('removed');

        return className.join(' ');
    }
}
